import { canSeeAddToCartButton, canSeeWishlistButton } from "~/utils/features";
import { displayBetterDeal, isProductPMA } from "~/domains/product/utils/productDescriptorUtils";
import { PRIMARY_BTN_VARIANT, SECONDARY_BTN_VARIANT, TERTIARY_BTN_VARIANT } from "~/common/components/Button/constants";
import { showManufacturerLogo } from "~/utils/product/showManufacturerLogo";
import { canSeeBuyFromSalesPartnerButton } from "~/features/salesPartnerStock/utils/salesPartnerStockHelper";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import { ProductService } from "~/features/addToCart/api/ProductService";
import { isMinOrderQuantityRestrictionEnabled } from "~/features/cart/cart/utils/cartUtils";
import type { PdpPageProductData } from "~/features/productDetails/models/PdpPageProductData";
import { userCanSeePriceReference } from "~/features/cart/cart/components/LineItemStaticParams";

interface Props {
  product: ProductDescriptor;
  qtyInCart: number;
  quantity?: number;
  packCode: string;
  packSize?: number;
  isOnSale?: boolean;
}

export const findMinOrderQuantity = (product: ProductDescriptor, packCode: string) => {
  const packagingInformation = ProductService.findPackagingByCode(product, packCode);
  return packagingInformation?.minOrderQuantity || 0;
};

export const usePdpSummaryLogic = ({ product, qtyInCart, packCode, packSize = 1, quantity = 0 }: Props) => {
  const { t } = useTranslation();

  const showBuyFromSalesPartnerButton = canSeeBuyFromSalesPartnerButton();
  const showCartButton = canSeeAddToCartButton();
  const showWishlistButton = canSeeWishlistButton();

  const variants = [PRIMARY_BTN_VARIANT, SECONDARY_BTN_VARIANT, TERTIARY_BTN_VARIANT, TERTIARY_BTN_VARIANT];
  let buyFromSalesPartnerButtonProps = {},
    cartButtonProps = {},
    wishlistButtonProps = {};

  if (showBuyFromSalesPartnerButton) {
    buyFromSalesPartnerButtonProps = variants.shift()!;
  }

  if (showCartButton) {
    cartButtonProps = variants.shift()!;
  }
  if (showWishlistButton) {
    wishlistButtonProps = variants.shift()!;
  }
  const configureButtonProps = variants.shift()!;

  const buttons = {
    showBuyFromSalesPartnerButton,
    showCartButton,
    showWishlistButton,
    buyFromSalesPartnerButtonProps,
    cartButtonProps,
    wishlistButtonProps,
    configureButtonProps,
  };

  let showBetterDeal = false;
  const isPriceLoaded = !!product.prices.length;
  if (isPriceLoaded) {
    const totalQty = quantity + qtyInCart;
    showBetterDeal = displayBetterDeal({ product: product, packCode }, totalQty);
  }

  const isPackageSelected = !!packCode;
  const isAvailabilityLoaded = !!product.availability;

  const minOrderQuantity = findMinOrderQuantity(product, packCode);

  const { isPackageSizeInvalid, MOQHelperLabel } = useMOQValidation({
    quantity,
    packSize,
    minOrderQuantity,
    isAvailabilityLoaded,
    isPackageSelected,
  });

  return {
    buttons,
    flags: {
      showBetterDeal,
      MOQHelperLabel,
      isPackageSizeInvalid,
      showManufacturerLogo: showManufacturerLogo(product.base.manufacturerLogoUrl, product.base.isOptime),
    },
  };
};

export const useMOQValidation = ({
  quantity,
  packSize,
  minOrderQuantity,
  isAvailabilityLoaded,
  isPackageSelected,
  isOnSale,
}: {
  quantity: number;
  packSize: number;
  minOrderQuantity: number;
  isAvailabilityLoaded: boolean;
  isPackageSelected: boolean;
  isOnSale?: boolean;
}) => {
  const { t } = useTranslation();

  const { isPackageSizeInvalid, MOQHelperLabel } = useMemo(() => {
    let isInvalid = false;
    let label = minOrderQuantity > 1 ? t("page.moq.helperText.moqNotMet", { minOrderQuantity }) : "";

    if (isAvailabilityLoaded && isPackageSelected) {
      if (isMinOrderQuantityRestrictionEnabled() && !isOnSale) {
        if (minOrderQuantity < packSize) {
          if (quantity === 0 || !Number.isInteger(quantity / packSize)) {
            isInvalid = true;
            label = t("page.moq.helperText.quantotyNotMet", { packSize });
          }
        } else {
          if (quantity < minOrderQuantity) {
            isInvalid = true;
            if (Number.isInteger(quantity / packSize)) {
              label = minOrderQuantity > 1 ? t("page.moq.helperText.moqNotMet", { minOrderQuantity }) : "";
            } else {
              label = t("page.moq.helperText.bothNotSatisfied", { minOrderQuantity, packSize });
            }
          } else {
            if (!Number.isInteger(quantity / packSize)) {
              isInvalid = true;
              label = t("page.moq.helperText.quantotyNotMet", { packSize });
            }
          }
        }
      } else {
        isInvalid = quantity === 0 || !Number.isInteger(quantity / packSize);
      }
    }

    return {
      isPackageSizeInvalid: isInvalid,
      MOQHelperLabel: label,
    };
  }, [quantity, packSize, minOrderQuantity, isAvailabilityLoaded, isPackageSelected, t]);

  return { isPackageSizeInvalid, MOQHelperLabel };
};

export const getDefaultQuantity = (packSize: number, minOrderQuantity: number) => {
  if (minOrderQuantity < packSize) {
    return packSize > 1 ? packSize : 1;
  } else {
    if (minOrderQuantity % packSize === 0) {
      // MOQ is a multiple of pack size
      return minOrderQuantity;
    } else {
      // MOQ is not a multiple of pack size
      return minOrderQuantity + (packSize - (minOrderQuantity % packSize));
    }
  }

  return packSize;
};

export const shouldShowPriceReference = (
  isExternalProduct: boolean | undefined,
  productDescriptor: ProductDescriptor<PdpPageProductData>,
) => !(isExternalProduct && isProductPMA(productDescriptor)) && userCanSeePriceReference();
